import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import type { EntitySnapshot } from "@models/data/entityData";
import {
  materialColorAlpha,
  materialColorHex,
  materialFloatMap,
  materialNumber,
  materialStringMap,
  type MaterialDto,
} from "@models/data/materialDto";
import type { IProjectModel } from "@models/project/IProjectModel";
import { publicUrl } from "@core/publicUrl";
import { loadMaterialTexture } from "./materialTextureCache";
import type {
  EditableMaterial,
  IEntitySourceMaterialHost,
  IMaterialModel,
  MaterialLibraryGroup,
  MaterialPatch,
  MaterialPreview,
} from "./IMaterialModel";
import {
  DEFAULT_MATERIAL_SHADER,
  deriveMaterialKeywords,
  materialSurfaceFromDto,
  materialTextureIds,
  materialWorkflowFromDto,
  normalizeMaterialShaderName,
  resolveMaterialShaderVariant,
} from "./materialShaderVariants";

const MATERIAL_LIBRARY_URL = publicUrl("materials/material-library.json");
const MATERIAL_PREVIEW_BASE_URL = publicUrl("materials/materialpreview");
const MATERIAL_TEXTURE_BASE_URL = publicUrl("materials/textures");
const MATERIAL_PREVIEW_ALIASES: Readonly<Record<string, string>> = {
  Default: "gray",
  Flat: "Unlit",
  Xray: "XRay",
  XRay: "XRay",
};
const DEFAULT_TILING = Object.freeze({ x: 1, y: 1 });
const DEFAULT_OFFSET = Object.freeze({ x: 0, y: 0 });

/**
 * First material-inspector slice backed by Unity's StreamingAssets
 * `material-library.json`.
 *
 * Project DTOs are hydrated into editable Campfire material metadata while
 * library entries remain immutable presets that can be dragged onto objects.
 */
export class MaterialModel implements IMaterialModel {
  readonly onMaterialChanged = new Emitter<{ materialId: string; entityId: string | null }>();
  readonly onActiveMaterialChanged = new Emitter<EditableMaterial | null>();
  readonly onLibraryChanged = new Emitter<void>();

  private readonly m_materials = new Map<string, EditableMaterial>();
  private readonly m_materialTargets = new Map<string, Set<string>>();
  private m_libraryGroups: ReadonlyArray<MaterialLibraryGroup> = [];
  private m_activeMaterialId: string | null = null;
  /**
   * Texture ids owned by the shared material library — the web equivalent of
   * the set `MaterialLibraryModel.ContainsTexture` answers against. Populated
   * from the same `material-library.json` manifest the groups come from, so a
   * library texture is never re-uploaded when an asset's materials are copied.
   */
  private readonly m_sharedTextureIds = new Set<string>();
  /** See `IEntitySourceMaterialHost`. Absent until a viewport mounts. */
  private m_sourceMaterialHost: IEntitySourceMaterialHost | null = null;
  /**
   * Entity ids whose synthesized material has been seeded from the renderer.
   *
   * The seed is retried until the host answers, because `ensureEntityMaterial`
   * routinely runs before the entity's glTF has finished downloading — and it
   * caches, so a one-shot attempt would pin the hashed-pastel fallback for the
   * rest of the session.
   */
  private readonly m_seededEntityMaterials = new Set<string>();

  constructor(private readonly m_project: IProjectModel) {
    void this.loadMaterialLibrary();
  }

  attachSourceMaterialHost(host: IEntitySourceMaterialHost | null): void {
    this.m_sourceMaterialHost = host;
    // A re-attach means a new renderer, so the entities are being rebuilt;
    // let every un-materialized entity re-seed against it.
    this.m_seededEntityMaterials.clear();
  }

  get activeMaterial(): EditableMaterial | null {
    return this.m_activeMaterialId ? this.getMaterial(this.m_activeMaterialId) : null;
  }

  getEntityMaterials(sceneId: string, snapshotId: string): ReadonlyArray<MaterialPreview> {
    const entity = this.m_project.getEntityBySnapshotId(sceneId, snapshotId);
    if (!entity) return [];

    const selectedSnapshot = findSnapshotById(entity.snapshots, snapshotId);
    const materialIds = selectedSnapshot
      ? uniqueMaterialIds(flattenSnapshots([selectedSnapshot]).flatMap((snapshot) => snapshot.materialIds))
      : [];

    const materials = materialIds.length > 0
      ? this.ensureProjectMaterials(entity.id, materialIds)
      : this.entityFallbackMaterials(sceneId, entity);
    return materials.map((material, index) => this.toMaterialPreview(material, index));
  }

  getSelectionSetMaterials(
    sceneId: string,
    memberSnapshotIds: ReadonlyArray<string>,
  ): ReadonlyArray<MaterialPreview> {
    const seen = new Set<string>();
    const out: MaterialPreview[] = [];
    for (const snapshotId of memberSnapshotIds) {
      for (const material of this.getEntityMaterials(sceneId, snapshotId)) {
        if (seen.has(material.id)) continue;
        seen.add(material.id);
        // The slot label numbers a material WITHIN one entity, so it is
        // meaningless once several are pooled — and misleading, since two
        // members would both claim "Material 1".
        out.push({ ...material, slotLabel: "" });
      }
    }
    return out;
  }

  getSnapshotMaterials(sceneId: string, snapshotId: string): ReadonlyArray<EditableMaterial> {
    const entity = this.m_project.getEntityBySnapshotId(sceneId, snapshotId);
    if (!entity) return [];
    const snapshot = findSnapshotById(entity.snapshots, snapshotId);
    if (!snapshot || snapshot.materialIds.length === 0) return [];
    return this.ensureProjectMaterials(entity.id, snapshot.materialIds);
  }

  getMaterialsForEntitySlots(
    entityId: string,
    materialIds: ReadonlyArray<string>,
  ): ReadonlyArray<EditableMaterial> {
    return this.ensureProjectMaterials(entityId, materialIds);
  }

  getEntityMaterial(sceneId: string, entityId: string): EditableMaterial | null {
    const entity = this.m_project.getEntityById(sceneId, entityId);
    if (!entity) return null;
    const materialId = entity.snapshots[0]?.materialIds[0] ?? null;
    if (materialId) return this.ensureProjectMaterial(entity.id, materialId);
    return this.ensureEntityMaterial(sceneId, entity.id, entity.assetId);
  }

  setActiveMaterial(materialId: string | null): void {
    this.m_activeMaterialId = materialId;
    this.onActiveMaterialChanged.emit(this.activeMaterial);
  }

  getMaterial(materialId: string): EditableMaterial | null {
    return this.m_materials.get(materialId) ?? null;
  }

  async updateMaterial(materialId: string, patch: MaterialPatch): Promise<void> {
    // Paint FIRST, fork second. The optimistic local apply below has always
    // been synchronous — it is what makes a slider drag track the pointer —
    // and the fork costs a cloud round trip, so doing it first would stall the
    // viewport on the first edit of every library-placed model.
    const current = this.m_materials.get(materialId);
    if (!current) return;
    const next = withDerivedMaterialFields({ ...current, ...patch });
    this.m_materials.set(materialId, next);
    // Close the seed window BEFORE announcing: `emitMaterialChanged` runs the
    // Look list synchronously, which re-enters `ensureEntityMaterial` — and a
    // first-time seed there would replace the value just typed with whatever
    // the renderer is still showing.
    const editedEntityId = entityIdFromMaterialId(materialId);
    if (editedEntityId) this.m_seededEntityMaterials.add(editedEntityId);
    this.emitMaterialChanged(materialId);
    if (this.m_activeMaterialId === materialId) {
      this.onActiveMaterialChanged.emit(next);
    }

    // A material row is customer-scoped, so a row still owned by an asset
    // library entry is the SAME row the library and every other project that
    // placed the asset reads. Fork before PERSISTING — see
    // `ProjectModelMaterialFork` for why this guard exists on web and not in
    // Unity. Costs one Set lookup for an already-private row, which is every
    // row on the normal path.
    let targetId = materialId;
    if (this.m_project.isLibraryOwnedMaterial(materialId)) {
      targetId = (await this.m_project.forkLibraryMaterialAsync(materialId)) ?? materialId;
      // `adoptForkedMaterial` seeds the copy from `m_materials.get(materialId)`,
      // which now already carries this edit.
      if (targetId !== materialId) this.adoptForkedMaterial(materialId, targetId);
    }

    // An entity that declares no `MaterialIDs` is shown a SYNTHESIZED material
    // with no row behind it, so the write below used to find no base DTO and
    // `return` — silently, after the local paint had already made the edit
    // look accepted. Mint the row instead and re-point the entity at it, which
    // is what the drag-a-library-material path (`applyLibraryMaterialToSnapshot`)
    // has always done for the same state.
    const materialization = this.m_project.getMaterialData(targetId)
      ? null
      : this.planEntityMaterialization(targetId);
    if (materialization) {
      targetId = materialization.materialId;
      this.adoptForkedMaterial(materialId, targetId);
    }

    const edited = this.m_materials.get(targetId) ?? next;
    const baseDto = this.m_project.getMaterialData(targetId)
      ?? (materialization ? defaultMaterialDto(targetId) : null);
    if (!baseDto) {
      // Not reachable from the inspector: Look only lists materials that
      // either resolve to a row or are the synthesized entity one handled
      // above. Warn rather than return in silence — an edit that persists
      // nothing while the UI shows it accepted is the defect this whole
      // branch was added to fix.
      console.warn(`[MaterialModel] no row behind material ${targetId}; edit not persisted`);
      return;
    }
    try {
      await this.m_project.updateMaterialDataAsync(editableMaterialToDto(edited, baseDto));
      // AFTER the row exists: a snapshot pointing at a row that failed to
      // write renders as an unresolvable material, which the renderer answers
      // with one flat colour over the whole model.
      if (materialization) {
        await this.m_project.updateEntitySnapshotAsync(
          materialization.sceneId,
          materialization.snapshotId,
          { materialIds: [targetId] },
        );
      }
    } catch (err) {
      // A fork and a materialization are mutually exclusive: a fork produces a
      // real row, which is exactly what makes `materialization` null above.
      if (materialization) {
        // Nothing else references a minted id that never reached the cloud;
        // leaving it would let the next edit write a row the entity does not
        // point at.
        this.m_materials.delete(targetId);
        this.m_materialTargets.delete(targetId);
        this.m_materials.set(materialId, current);
        this.registerMaterialTarget(materialId, materialization.entityId);
        if (this.m_activeMaterialId === targetId) {
          this.m_activeMaterialId = materialId;
          this.onActiveMaterialChanged.emit(current);
        }
        this.emitMaterialChanged(materialId);
        throw err;
      }
      // Re-key onto `targetId`: after a fork `current` still carries the OLD
      // id, and the view reads `activeMaterial.id` for its next edit — which
      // would send the library id back in and fork a second time.
      const reverted = targetId === materialId ? current : { ...current, id: targetId };
      this.m_materials.set(targetId, reverted);
      this.emitMaterialChanged(targetId);
      if (this.m_activeMaterialId === targetId) {
        this.onActiveMaterialChanged.emit(reverted);
      }
      // A fork left the pre-edit entry under the OLD id, which is the
      // library's row; roll that back too so a failed write cannot leave the
      // library looking edited in this session's cache.
      if (targetId !== materialId) {
        this.m_materials.set(materialId, current);
        this.emitMaterialChanged(materialId);
      }
      throw err;
    }
  }

  /**
   * Assign or clear one texture slot. See {@link IMaterialModel.setMaterialTexture}.
   *
   * The UPLOAD happens before anything local changes, deliberately: a slot
   * pointing at an id whose object never landed renders as a missing texture
   * for every client, permanently, and is indistinguishable from an orphaned
   * Unity row. If the PUT throws, nothing has been touched and the caller can
   * report it.
   *
   * A CLEAR drops both the `Textures` and the `FilterModes` entry — Unity
   * rebuilds both maps from scratch in `FromMaterial` (`MaterialData.cs:406`),
   * so a stale filter mode for an unbound slot cannot survive there either.
   * The S3 object is deliberately NOT deleted; see the interface note.
   */
  async setMaterialTexture(materialId: string, slot: string, file: Blob | null): Promise<void> {
    const current = this.m_materials.get(materialId);
    if (!current || !slot) return;

    let textureId: string | null = null;
    if (file) {
      textureId = await this.m_project.uploadMaterialTextureAsync(file);
      if (!textureId) {
        throw new Error("Textures cannot be uploaded while the project is offline.");
      }
    }

    const textureIds = { ...current.textureIds };
    const textureFilterModes = { ...current.textureFilterModes };
    if (textureId) {
      textureIds[slot] = textureId;
      // Unity's picker builds a `Texture2D` at Unity's own default filter
      // (Bilinear) and `CopyTextureToMaterialData` records `texture.filterMode`
      // verbatim, so a freshly picked texture always lands as Bilinear.
      textureFilterModes[slot] = "Bilinear";
    } else {
      delete textureIds[slot];
      delete textureFilterModes[slot];
    }

    await this.updateMaterial(materialId, { textureIds, textureFilterModes });
  }

  /** See {@link IMaterialModel.resolveTextureUrl}. */
  async resolveTextureUrl(textureId: string): Promise<string | null> {
    if (!textureId) return null;
    const bundled = bundledMaterialTextureUrl(textureId);
    if (bundled) return bundled;
    const customerId = this.m_project.getMaterialTextureCustomerId();
    if (!customerId) return null;
    return loadMaterialTexture(customerId, textureId);
  }

  /**
   * Where to put the real row for a synthesized entity material, or null when
   * the id is not one (an unhydrated project row, a library preset) and the
   * caller should keep its existing behaviour.
   *
   * The ROOT snapshot is the target because the synthesized material is
   * entity-wide — that is the scope the Look page presents it at, and
   * `applyThreeMaterialsToNode` walks the root node's whole subtree, so one
   * assignment reaches every mesh exactly as the old renderer fallback did.
   */
  private planEntityMaterialization(materialId: string): {
    readonly materialId: string;
    readonly sceneId: string;
    readonly snapshotId: string;
    readonly entityId: string;
  } | null {
    const entityId = entityIdFromMaterialId(materialId);
    if (!entityId) return null;
    const sceneId = this.m_project.activeSceneId;
    if (!sceneId) return null;
    const entity = this.m_project.getEntityById(sceneId, entityId);
    const root = entity?.snapshots.find((snapshot) => snapshot.isRoot) ?? entity?.snapshots[0];
    if (!entity || !root) return null;
    return { materialId: createMaterialId(), sceneId, snapshotId: root.id, entityId };
  }

  /**
   * Move this model's editing state from a forked-away library material id
   * onto the project's private copy.
   *
   * The old id is deliberately LEFT in `m_materials`: it is still the
   * library's row, other assets may legitimately reference it, and a fork
   * from one place must not evict it from the cache for another. Only the
   * TARGETS move, plus the active selection so the open inspector keeps
   * editing the same visual material rather than silently detaching.
   */
  private adoptForkedMaterial(sourceId: string, copyId: string): void {
    // `updateMaterial` returns early unless the source is hydrated, and it
    // applies the edit before forking — so `source` is always present here and
    // already carries the pending edit, which is what moves onto the copy.
    if (!this.m_materials.has(copyId)) {
      const source = this.m_materials.get(sourceId);
      if (source) this.m_materials.set(copyId, { ...source, id: copyId });
    }
    const targets = this.m_materialTargets.get(sourceId);
    if (targets) {
      this.m_materialTargets.set(copyId, new Set(targets));
      this.m_materialTargets.delete(sourceId);
    }
    if (this.m_activeMaterialId === sourceId) {
      this.m_activeMaterialId = copyId;
      this.onActiveMaterialChanged.emit(this.getMaterial(copyId));
    }
    // Repaint the parts that just changed material id.
    this.emitMaterialChanged(copyId);
  }

  getLibraryGroups(): ReadonlyArray<MaterialLibraryGroup> {
    return this.m_libraryGroups;
  }

  /**
   * Port of `ProjectDataModel_Utils.IsSharedTexture` →
   * `IMaterialLibraryModel.ContainsTexture` (`ProjectDataModel_Utils.cs:43`).
   * A texture owned by the shared library is referenced, never copied, when an
   * asset's materials are duplicated (Save to Library). Unity returns `false`
   * when the library model is absent; the web returns `false` before the
   * manifest resolves, matching that fallback.
   */
  isSharedTexture(textureId: string): boolean {
    return this.m_sharedTextureIds.has(textureId);
  }

  async applyLibraryMaterial(targetMaterialId: string, libraryMaterialId: string): Promise<void> {
    const source = this.getMaterial(libraryMaterialId);
    if (!source) return;
    await this.updateMaterial(targetMaterialId, {
      // The preset's stored NAME, not its label — this is a whole-material
      // swap (Unity `MaterialModel.SwapMaterial`), so the target takes the
      // library entry's identity as the row records it.
      name: source.name,
      thumbnailUrl: source.thumbnailUrl,
      shader: source.shader,
      shaderVariant: source.shaderVariant,
      workflow: source.workflow,
      surface: source.surface,
      baseColor: source.baseColor,
      baseColorAlpha: source.baseColorAlpha,
      specColor: source.specColor,
      specColorAlpha: source.specColorAlpha,
      emissionColor: source.emissionColor,
      emissionColorAlpha: source.emissionColorAlpha,
      fresnelColor: source.fresnelColor,
      fresnelColorAlpha: source.fresnelColorAlpha,
      fresnelPower: source.fresnelPower,
      metallic: source.metallic,
      smoothness: source.smoothness,
      smoothnessLow: source.smoothnessLow,
      normalScale: source.normalScale,
      occlusionStrength: source.occlusionStrength,
      clearCoat: source.clearCoat,
      clearCoatGlossiness: source.clearCoatGlossiness,
      clearCoatGlossinessScale: source.clearCoatGlossinessScale,
      alphaCutoff: source.alphaCutoff,
      useVertexColor: source.useVertexColor,
      doubleFaced: source.doubleFaced,
      textureIds: source.textureIds,
      textureFilterModes: source.textureFilterModes,
      tiling: source.tiling,
      offset: source.offset,
      keywords: source.keywords,
    });
  }

  async applyLibraryMaterialToSnapshot(
    sceneId: string,
    snapshotId: string,
    libraryMaterialId: string,
  ): Promise<void> {
    const entity = this.m_project.getEntityBySnapshotId(sceneId, snapshotId);
    if (!entity) return Promise.resolve();
    const snapshot = findSnapshotById(entity.snapshots, snapshotId);
    const source = this.getMaterial(libraryMaterialId);
    if (!source || !snapshot) return;

    // A Workflow material preset only needs a temporary assignment. Point at
    // the already-loaded library row in memory; the playback ledger restores
    // the original ids. This avoids minting an orphan row for an unmaterialized
    // snapshot and avoids mutating/forking an existing row merely to preview
    // the preset.
    if (this.m_project.isWorkflowPlaybackMutationScopeActive) {
      await this.m_project.updateEntitySnapshotAsync(sceneId, snapshot.id, {
        materialIds: [libraryMaterialId],
      });
      return;
    }

    const existingMaterialId = snapshot.materialIds[0] ?? null;
    if (existingMaterialId) {
      this.ensureProjectMaterial(entity.id, existingMaterialId);
      await this.applyLibraryMaterial(existingMaterialId, libraryMaterialId);
      return;
    }

    const materialId = createMaterialId();
    const next = { ...source, id: materialId };
    const dto = editableMaterialToDto(next, defaultMaterialDto(materialId));
    this.m_materials.set(materialId, next);
    this.registerMaterialTarget(materialId, entity.id);
    this.emitMaterialChanged(materialId);
    if (this.m_activeMaterialId === libraryMaterialId) {
      this.setActiveMaterial(materialId);
    }

    try {
      await this.m_project.updateMaterialDataAsync(dto);
      await this.m_project.updateEntitySnapshotAsync(sceneId, snapshot.id, {
        materialIds: [materialId],
      });
    } catch (err) {
      this.m_materials.delete(materialId);
      this.m_materialTargets.delete(materialId);
      this.m_project.applyEntitySnapshotChanges(sceneId, snapshot);
      this.onMaterialChanged.emit({ materialId, entityId: entity.id });
      throw err;
    }
  }

  /**
   * The materials to show for a snapshot subtree that declares none.
   *
   * Prefers whatever the ENTITY already declares — after a materialize, that
   * is the root's real row — so selecting a child part keeps editing the same
   * material instead of being handed the synthetic one again (which would
   * materialize a second row onto the same root on its first edit).
   */
  private entityFallbackMaterials(
    sceneId: string,
    entity: { id: string; assetId: string | null; snapshots: ReadonlyArray<EntitySnapshot> },
  ): ReadonlyArray<EditableMaterial> {
    // Check the ROOT first: that is where a materialized material lands, so
    // the common case answers without walking the tree — which for a CAD
    // assembly is hundreds of nodes, on a path that used to be a Map lookup.
    const root = entity.snapshots.find((snapshot) => snapshot.isRoot) ?? entity.snapshots[0];
    const declared = root && root.materialIds.length > 0
      ? uniqueMaterialIds(root.materialIds)
      : uniqueMaterialIds(
        flattenSnapshots(entity.snapshots).flatMap((snapshot) => snapshot.materialIds),
      );
    if (declared.length > 0) {
      const resolved = this.ensureProjectMaterials(entity.id, declared);
      if (resolved.length > 0) return resolved;
    }
    return [this.ensureEntityMaterial(sceneId, entity.id, entity.assetId)];
  }

  private ensureEntityMaterial(
    sceneId: string | null,
    entityId: string,
    assetId: string | null,
  ): EditableMaterial {
    const id = entityMaterialId(entityId);
    const sample = this.m_seededEntityMaterials.has(entityId) || !sceneId
      ? null
      : this.m_sourceMaterialHost?.readEntitySourceMaterial(sceneId, entityId) ?? null;
    if (sample) this.m_seededEntityMaterials.add(entityId);

    const existing = this.m_materials.get(id);
    // Re-seed an entry built before the entity finished rendering; leave one
    // the renderer has already described alone, or a repaint would discard
    // whatever the user has typed since.
    if (existing && !sample) return existing;

    const material = editableMaterialFromMetadata({
      id,
      displayName: assetId ? "Imported Material" : "Default Material",
      shader: DEFAULT_MATERIAL_SHADER,
      workflow: "Metallic",
      surface: sample?.surface ?? "Opaque",
      // Falls back to the hashed pastel only when the renderer cannot answer
      // — an unbuilt entity, or a headless run.
      baseColor: sample?.baseColor ?? colorFromString(assetId ?? entityId),
      baseColorAlpha: sample?.baseColorAlpha ?? 1,
      metallic: sample?.metallic ?? 0,
      smoothness: sample?.smoothness ?? 50,
      doubleFaced: sample?.doubleFaced ?? false,
    });
    this.m_materials.set(id, material);
    this.registerMaterialTarget(id, entityId);
    if (existing) this.emitMaterialChanged(id);
    return material;
  }

  private ensureProjectMaterials(
    entityId: string,
    materialIds: ReadonlyArray<string>,
  ): ReadonlyArray<EditableMaterial> {
    return materialIds
      .map((materialId) => this.ensureProjectMaterial(entityId, materialId))
      .filter((material): material is EditableMaterial => material !== null);
  }

  private ensureProjectMaterial(entityId: string, materialId: string): EditableMaterial | null {
    const existing = this.m_materials.get(materialId);
    if (existing) {
      this.registerMaterialTarget(materialId, entityId);
      return existing;
    }
    const dto = this.m_project.getMaterialData(materialId);
    if (!dto) return null;
    const material = materialDtoToEditable(dto);
    this.m_materials.set(material.id, material);
    this.registerMaterialTarget(material.id, entityId);
    return material;
  }

  private toMaterialPreview(material: EditableMaterial, index: number): MaterialPreview {
    return {
      id: material.id,
      displayName: materialDisplayName(material.displayName, material.id, material.shader),
      previewColor: material.baseColor,
      shader: material.shader,
      shaderFamily: material.shaderFamily,
      slotLabel: `Material ${index + 1}`,
      thumbnailUrl: material.thumbnailUrl,
    };
  }

  private registerMaterialTarget(materialId: string, entityId: string): void {
    const targets = this.m_materialTargets.get(materialId) ?? new Set<string>();
    targets.add(entityId);
    this.m_materialTargets.set(materialId, targets);
  }

  private emitMaterialChanged(materialId: string): void {
    const targets = this.m_materialTargets.get(materialId);
    const entityId = targets && targets.size === 1 ? [...targets][0] : null;
    this.onMaterialChanged.emit({ materialId, entityId });
  }

  private async loadMaterialLibrary(): Promise<void> {
    try {
      const response = await fetch(MATERIAL_LIBRARY_URL);
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
      const library = await response.json() as UnityMaterialLibraryInfo;
      const materialDataById = new Map<string, UnityMaterialData>();
      this.m_sharedTextureIds.clear();
      for (const materialData of library.materialData ?? []) {
        if (materialData.id) materialDataById.set(materialData.id, materialData);
        for (const textureId of Object.values(stringMapFromUnityPairs(materialData.textures))) {
          if (textureId) this.m_sharedTextureIds.add(textureId);
        }
      }

      this.m_libraryGroups = (library.rootGroup?.subgroups ?? [])
        .map((group, index) => this.mapLibraryGroup(group, materialDataById, `${index}`))
        .filter((group) => group.materials.length > 0);
      this.onLibraryChanged.emit();
    } catch (err) {
      console.warn("[MaterialModel] failed to load Unity material library", err);
      this.m_libraryGroups = [];
      this.onLibraryChanged.emit();
    }
  }

  private mapLibraryGroup(
    group: UnityMaterialGroup,
    materialDataById: ReadonlyMap<string, UnityMaterialData>,
    key: string,
  ): MaterialLibraryGroup {
    const childMaterials = (group.subgroups ?? [])
      .flatMap((subgroup, index) =>
        this.mapLibraryGroup(subgroup, materialDataById, `${key}.${index}`).materials,
      );
    const materials = [
      ...(group.materials ?? []).map((materialId) => this.toPreview(materialId, materialDataById)),
      ...childMaterials,
    ].filter((material): material is MaterialPreview => material !== null);
    return {
      id: `${key}:${group.name}`,
      name: group.name,
      materials,
    };
  }

  private toPreview(
    materialId: string,
    materialDataById: ReadonlyMap<string, UnityMaterialData>,
  ): MaterialPreview | null {
    const materialData = materialDataById.get(materialId);
    if (!materialData) return null;
    const material = this.ensureLibraryMaterial(materialData);
    return {
      id: material.id,
      displayName: material.displayName,
      previewColor: material.baseColor,
      shader: material.shader,
      shaderFamily: material.shaderFamily,
      // Unity writes previews to StreamingAssets/materialpreview/{materialID}.png.
      // The UI falls back to `previewColor` if a preview image is missing.
      thumbnailUrl: material.thumbnailUrl,
    };
  }

  private ensureLibraryMaterial(materialData: UnityMaterialData): EditableMaterial {
    const id = materialData.id;
    const existing = this.m_materials.get(id);
    if (existing) return existing;

    const material = editableMaterialFromMetadata({
      id,
      displayName: materialDisplayName(materialData.name, id, materialData.shaderName),
      thumbnailUrl: materialPreviewUrl(id),
      shader: materialData.shaderName || DEFAULT_MATERIAL_SHADER,
      workflow: materialData.workflowMode === 1 ? "Metallic" : "Specular",
      surface: materialData.surface === 1 ? "Transparent" : "Opaque",
      baseColor: colorMapToHex(materialData.baseColor) ?? colorFromString(id),
      baseColorAlpha: alphaFromColorMap(materialData.baseColor, 1),
      specColor: colorMapToHex(materialData.specColor) ?? "#333333",
      emissionColor: colorMapToHex(materialData.emissionColor) ?? "#000000",
      emissionColorAlpha: alphaFromColorMap(materialData.emissionColor, 0),
      fresnelColor: colorMapToHex(materialData.fresnelColor) ?? "#000000",
      fresnelPower: materialData.fresnelPower ?? 0,
      metallic: normalizedToPercent(materialData.metallic),
      smoothness: normalizedToPercent(materialData.smoothness),
      smoothnessLow: normalizedToPercent(materialData.smoothnessLow),
      normalScale: materialData.bumpScale ?? 0,
      // ⚠ Defaults to 1 when the payload states neither spelling — the same
      // rule, and the same reason, as the Dynamo path in
      // {@link materialDtoToEditable}. `normalizedToPercent` answers 0 for a
      // non-finite input, so this cannot be left to it.
      occlusionStrength: normalizedToPercent(
        materialData.occlusionStrength ?? materialData.occulusionStrength ?? 1,
      ),
      clearCoat: normalizedToPercent(materialData.clearCoat),
      clearCoatGlossiness: normalizedToPercent(materialData.clearCoatGlossiness),
      clearCoatGlossinessScale: normalizedToPercent(materialData.clearCoatGlossinessScale),
      alphaCutoff: materialData.alphaCutoff ?? materialData.cutoff ?? 0.01,
      useVertexColor: materialData.useVertexColor === 1,
      doubleFaced: materialData.cull === 0,
      textureIds: stringMapFromUnityPairs(materialData.textures),
      textureFilterModes: stringMapFromUnityPairs(materialData.filterModes),
      tiling: vector2FromUnityPairs(materialData.tiling, DEFAULT_TILING),
      offset: vector2FromUnityPairs(materialData.offset, DEFAULT_OFFSET),
    });
    this.m_materials.set(id, material);
    return material;
  }
}

function entityMaterialId(entityId: string): string {
  return `entity:${entityId}:default`;
}

/** Inverse of {@link entityMaterialId}; null for any other id shape. */
function entityIdFromMaterialId(materialId: string): string | null {
  const match = /^entity:(.+):default$/.exec(materialId);
  return match?.[1] ?? null;
}

function materialPreviewUrl(materialId: string): string {
  return `${MATERIAL_PREVIEW_BASE_URL}/${encodeURIComponent(
    MATERIAL_PREVIEW_ALIASES[materialId] ?? materialId,
  )}.png`;
}

function materialDisplayName(
  name: string | null | undefined,
  id: string,
  shaderName: string | null | undefined,
): string {
  const candidates = [name, id];
  for (const candidate of candidates) {
    const decoded = decodeMaterialName(candidate);
    if (decoded) return decoded;
  }
  for (const candidate of candidates) {
    const normalized = normalizeMaterialName(candidate);
    if (normalized) return normalized;
  }
  return readableShaderName(shaderName) ?? "Material";
}

function normalizeMaterialName(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || isIdLikeMaterialName(trimmed)) return null;
  return trimmed;
}

function decodeMaterialName(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || !looksLikeEncodedJson(trimmed)) return null;
  try {
    const json = new TextDecoder().decode(base64ToBytes(trimmed));
    const parsed = JSON.parse(json) as unknown;
    return nameFromDecodedMaterialId(parsed);
  } catch {
    return null;
  }
}

function nameFromDecodedMaterialId(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const keys = ["name", "Name", "n", "displayName", "DisplayName", "materialName", "MaterialName"];
  for (const key of keys) {
    const normalized = normalizeMaterialName(typeof record[key] === "string" ? record[key] : undefined);
    if (normalized) return normalized;
  }
  return null;
}

function looksLikeEncodedJson(value: string): boolean {
  return value.startsWith("eyJ") || value.startsWith("e30");
}

function isIdLikeMaterialName(value: string): boolean {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return true;
  }
  if (looksLikeEncodedJson(value)) return true;
  return value.length > 32 && !/\s/.test(value);
}

function base64ToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function readableShaderName(shaderName: string | null | undefined): string | null {
  if (!shaderName) return null;
  return shaderName.replace(/^Shader Graphs\//, "").replace(/^Campfire\//, "");
}

function materialDtoToEditable(dto: MaterialDto): EditableMaterial {
  const id = dto.ID;
  return editableMaterialFromMetadata({
    id,
    // The row's own value, untouched — `displayName` beside it is the label.
    name: typeof dto.Name === "string" ? dto.Name : "",
    displayName: materialDisplayName(dto.Name, id, dto.ShaderName),
    thumbnailUrl: materialPreviewUrl(id),
    shader: dto.ShaderName || DEFAULT_MATERIAL_SHADER,
    workflow: materialWorkflowFromDto(dto),
    surface: materialSurfaceFromDto(dto),
    baseColor: materialColorHex(dto.BaseColor, colorFromString(id)),
    baseColorAlpha: materialColorAlpha(dto.BaseColor, 1),
    specColor: materialColorHex(dto.SpecColor, "#333333"),
    specColorAlpha: materialColorAlpha(dto.SpecColor, 1),
    emissionColor: materialColorHex(dto.EmissionColor, "#000000"),
    emissionColorAlpha: materialColorAlpha(dto.EmissionColor, 0),
    fresnelColor: materialColorHex(dto.FresnelColor, "#000000"),
    fresnelColorAlpha: materialColorAlpha(dto.FresnelColor, 1),
    fresnelPower: materialNumber(dto.FresnelPower, 0),
    metallic: normalizedToPercent(materialNumber(dto.Metallic, 0)),
    smoothness: normalizedToPercent(materialNumber(dto.Smoothness, 0)),
    smoothnessLow: normalizedToPercent(materialNumber(dto.SmoothnessLow, 0)),
    normalScale: materialNumber(dto.BumpScale, 0),
    // ⚠ An ABSENT `OcclusionStrength` defaults to 1, not 0, and the 0 it
    // replaces was the darkest legal reading of a state Unity treats as
    // corrupt: `DynamoClient.cs:1323` reads the attribute with an UNGUARDED
    // indexer, so a row without it does not load on desktop at all. Inventing
    // a value for it is already a courtesy; inventing the one that removes all
    // indirect light is not. 332 of the 335 shipped library rows carry 1.
    //
    // It matters most for a `flat` shader — fourteen of the twenty-three graphs
    // — where the value scales `envMapIntensity` directly and
    // `CAMPFIRE_AMBIENT_LIGHT_INTENSITY` is 0, so `scene.environment` is the
    // ONLY indirect light and a 0 leaves a metal with no diffuse and no
    // reflection, i.e. black.
    occlusionStrength: normalizedToPercent(materialNumber(dto.OcclusionStrength, 1)),
    clearCoat: normalizedToPercent(materialNumber(dto.ClearCoat, 0)),
    clearCoatGlossiness: normalizedToPercent(materialNumber(dto.ClearCoatGlossiness, 0)),
    clearCoatGlossinessScale: normalizedToPercent(materialNumber(dto.ClearCoatGlossinessScale, 0)),
    alphaCutoff: materialNumber(dto.Cutoff, 0.01),
    useVertexColor: materialNumber(dto.UseVertexColor, 0) === 1,
    doubleFaced: materialNumber(dto.Cull, 2) === 0,
    textureIds: materialTextureIds(dto),
    textureFilterModes: materialStringMap(dto.FilterModes),
    tiling: vector2FromMaterialMap(dto.Tiling, DEFAULT_TILING),
    offset: vector2FromMaterialMap(dto.Offset, DEFAULT_OFFSET),
  });
}

function editableMaterialToDto(material: EditableMaterial, base: MaterialDto): MaterialDto {
  return {
    ...base,
    ID: material.id,
    // NEVER `displayName` — that is a derived label, and writing it renames a
    // GUID-named Unity material to its shader on the first unrelated edit.
    Name: material.name,
    ShaderName: material.shader,
    UserPreset: base.UserPreset ?? false,
    WorkflowMode: material.workflow === "Metallic" ? 1 : 0,
    Surface: material.surface === "Transparent" ? 1 : 0,
    Cull: material.doubleFaced ? 0 : 2,
    Smoothness: percentToNormalized(material.smoothness),
    Metallic: percentToNormalized(material.metallic),
    BaseColor: hexToColorMap(material.baseColor, material.baseColorAlpha),
    SpecColor: hexToColorMap(material.specColor, material.specColorAlpha),
    EmissionColor: hexToColorMap(material.emissionColor, material.emissionColorAlpha),
    FresnelColor: hexToColorMap(material.fresnelColor, material.fresnelColorAlpha),
    FresnelPower: material.fresnelPower,
    BumpScale: material.normalScale,
    SmoothnessLow: percentToNormalized(material.smoothnessLow),
    OcclusionStrength: percentToNormalized(material.occlusionStrength),
    ClearCoat: percentToNormalized(material.clearCoat),
    ClearCoatGlossiness: percentToNormalized(material.clearCoatGlossiness),
    Cutoff: material.alphaCutoff,
    UseVertexColor: material.useVertexColor ? 1 : 0,
    Textures: { ...material.textureIds },
    FilterModes: { ...material.textureFilterModes },
    Tiling: { ...material.tiling },
    Offset: { ...material.offset },
  };
}

function defaultMaterialDto(id: string): MaterialDto {
  return {
    ID: id,
    Name: id,
    ShaderName: DEFAULT_MATERIAL_SHADER,
    UserPreset: false,
    WorkflowMode: 1,
    Surface: 0,
    Cull: 2,
    Smoothness: 0.5,
    Metallic: 0,
    BaseColor: { r: 1, g: 1, b: 1, a: 1 },
    SpecColor: { r: 0.2, g: 0.2, b: 0.2, a: 1 },
    EmissionColor: { r: 0, g: 0, b: 0, a: 0 },
    FresnelColor: { r: 0, g: 0, b: 0, a: 1 },
    FresnelPower: 0,
    BumpScale: 0,
    SmoothnessLow: 0,
    OcclusionStrength: 0,
    ClearCoat: 0,
    ClearCoatGlossiness: 0,
    ClearCoatGlossinessScale: 0,
    Cutoff: 0.01,
    UseVertexColor: 0,
    Textures: {},
    FilterModes: {},
    Tiling: { x: 1, y: 1 },
    Offset: { x: 0, y: 0 },
  };
}

function createMaterialId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? randomUuid()
    : `material.${Date.now()}.${Math.random().toString(36).slice(2)}`;
}

/**
 * Inspector percent -> row float.
 *
 * ⚠ Deliberately NOT clamped, and its partner {@link normalizedToPercent} is
 * deliberately NOT rounded. The pair is applied to seven scalars on every
 * whole-item write, so a `Math.round` there quantized a Unity-authored
 * `Smoothness: 0.375` to `0.38` the moment ANY other field on the row was
 * touched, and a clamp destroyed an out-of-range value outright. Unity's
 * sliders bind straight to `UnityEngine.UI.Slider.value` over a continuous
 * 0..1 range with no adapter and no clamp (`Content/Controls/Slider.prefab:265`),
 * so an untouched field has to survive the round trip verbatim. Rounding for
 * DISPLAY is the view's job.
 */
function percentToNormalized(value: number): number {
  return Number.isFinite(value) ? value / 100 : 0;
}

function hexToColorMap(hex: string, alpha = 1): Record<string, number> {
  const normalized = hex.trim().replace(/^#/, "");
  if (!/^[\da-f]{6}$/i.test(normalized)) {
    return { r: 1, g: 1, b: 1, a: alpha };
  }
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16) / 255,
    g: Number.parseInt(normalized.slice(2, 4), 16) / 255,
    b: Number.parseInt(normalized.slice(4, 6), 16) / 255,
    a: alpha,
  };
}

interface EditableMaterialMetadata {
  readonly id: string;
  /** Stored `MaterialData.Name`. Falls back to `displayName` when absent. */
  readonly name?: string;
  readonly displayName: string;
  readonly thumbnailUrl?: string;
  readonly shader: string;
  readonly workflow: EditableMaterial["workflow"];
  readonly surface: EditableMaterial["surface"];
  readonly baseColor: string;
  readonly baseColorAlpha?: number;
  readonly specColor?: string;
  readonly specColorAlpha?: number;
  readonly emissionColor?: string;
  readonly emissionColorAlpha?: number;
  readonly fresnelColor?: string;
  readonly fresnelColorAlpha?: number;
  readonly fresnelPower?: number;
  readonly metallic?: number;
  readonly smoothness?: number;
  readonly smoothnessLow?: number;
  readonly normalScale?: number;
  readonly occlusionStrength?: number;
  readonly clearCoat?: number;
  readonly clearCoatGlossiness?: number;
  readonly clearCoatGlossinessScale?: number;
  readonly alphaCutoff?: number;
  readonly useVertexColor?: boolean;
  readonly doubleFaced?: boolean;
  readonly textureIds?: Readonly<Record<string, string>>;
  readonly textureFilterModes?: Readonly<Record<string, string>>;
  readonly tiling?: Readonly<{ x: number; y: number }>;
  readonly offset?: Readonly<{ x: number; y: number }>;
}

function editableMaterialFromMetadata(metadata: EditableMaterialMetadata): EditableMaterial {
  return withDerivedMaterialFields({
    id: metadata.id,
    name: metadata.name ?? metadata.displayName,
    displayName: metadata.displayName,
    thumbnailUrl: metadata.thumbnailUrl,
    shader: metadata.shader,
    shaderVariant: "campfire-lit",
    shaderFamily: "lit",
    isConfigurableShader: true,
    workflow: metadata.workflow,
    surface: metadata.surface,
    baseColor: metadata.baseColor,
    baseColorAlpha: metadata.baseColorAlpha ?? 1,
    specColor: metadata.specColor ?? "#333333",
    specColorAlpha: metadata.specColorAlpha ?? 1,
    emissionColor: metadata.emissionColor ?? "#000000",
    emissionColorAlpha: metadata.emissionColorAlpha ?? 0,
    fresnelColor: metadata.fresnelColor ?? "#000000",
    fresnelColorAlpha: metadata.fresnelColorAlpha ?? 1,
    fresnelPower: metadata.fresnelPower ?? 0,
    metallic: metadata.metallic ?? 0,
    smoothness: metadata.smoothness ?? 50,
    smoothnessLow: metadata.smoothnessLow ?? 0,
    normalScale: metadata.normalScale ?? 0,
    occlusionStrength: metadata.occlusionStrength ?? 0,
    clearCoat: metadata.clearCoat ?? 0,
    clearCoatGlossiness: metadata.clearCoatGlossiness ?? 0,
    clearCoatGlossinessScale: metadata.clearCoatGlossinessScale ?? 0,
    alphaCutoff: metadata.alphaCutoff ?? 0.01,
    useVertexColor: metadata.useVertexColor ?? false,
    doubleFaced: metadata.doubleFaced ?? false,
    textureIds: metadata.textureIds ?? {},
    textureFilterModes: metadata.textureFilterModes ?? {},
    tiling: metadata.tiling ?? DEFAULT_TILING,
    offset: metadata.offset ?? DEFAULT_OFFSET,
    keywords: deriveMaterialKeywords({
      shaderName: metadata.shader,
      workflow: metadata.workflow,
      surface: metadata.surface,
      emissionColorAlpha: metadata.emissionColorAlpha ?? 0,
      textureIds: metadata.textureIds ?? {},
    }),
  });
}

function withDerivedMaterialFields(material: EditableMaterial): EditableMaterial {
  const shader = normalizeMaterialShaderName(material.shader);
  const variant = resolveMaterialShaderVariant(shader);
  const workflow = variant.supportsMetallicWorkflow ? material.workflow : "Specular";
  return {
    ...material,
    shader: variant.shaderName,
    shaderVariant: variant.id,
    shaderFamily: variant.family,
    isConfigurableShader: variant.isConfigurable,
    workflow,
    keywords: deriveMaterialKeywords({
      shaderName: variant.shaderName,
      workflow,
      surface: material.surface,
      emissionColorAlpha: material.emissionColorAlpha,
      textureIds: material.textureIds,
    }),
  };
}

function findSnapshotById(
  snapshots: ReadonlyArray<EntitySnapshot>,
  snapshotId: string,
): EntitySnapshot | null {
  for (const snapshot of snapshots) {
    if (snapshot.id === snapshotId) return snapshot;
    const child = findSnapshotById(snapshot.children, snapshotId);
    if (child) return child;
  }
  return null;
}

function flattenSnapshots(snapshots: ReadonlyArray<EntitySnapshot>): EntitySnapshot[] {
  const out: EntitySnapshot[] = [];
  const walk = (snapshot: EntitySnapshot) => {
    out.push(snapshot);
    for (const child of snapshot.children) walk(child);
  };
  for (const snapshot of snapshots) walk(snapshot);
  return out;
}

function uniqueMaterialIds(materialIds: ReadonlyArray<string>): ReadonlyArray<string> {
  return [...new Set(materialIds.filter(Boolean))];
}

interface UnityMaterialLibraryInfo {
  readonly rootGroup?: UnityMaterialGroup;
  readonly materialData?: ReadonlyArray<UnityMaterialData>;
}

interface UnityMaterialGroup {
  readonly name: string;
  readonly materials?: ReadonlyArray<string>;
  readonly subgroups?: ReadonlyArray<UnityMaterialGroup>;
}

interface UnityMaterialData {
  readonly name: string;
  readonly id: string;
  readonly workflowMode?: number;
  readonly surface?: number;
  readonly cull?: number;
  readonly shaderName?: string;
  readonly smoothness?: number;
  readonly metallic?: number;
  readonly baseColor?: ReadonlyArray<UnityNamedFloat>;
  readonly specColor?: ReadonlyArray<UnityNamedFloat>;
  readonly emissionColor?: ReadonlyArray<UnityNamedFloat>;
  readonly fresnelColor?: ReadonlyArray<UnityNamedFloat>;
  readonly fresnelPower?: number;
  readonly tiling?: ReadonlyArray<UnityNamedFloat>;
  readonly offset?: ReadonlyArray<UnityNamedFloat>;
  readonly textures?: ReadonlyArray<UnityNamedString>;
  readonly filterModes?: ReadonlyArray<UnityNamedString>;
  readonly bumpScale?: number;
  readonly smoothnessLow?: number;
  readonly occlusionStrength?: number;
  readonly occulusionStrength?: number;
  readonly clearCoat?: number;
  readonly clearCoatGlossiness?: number;
  readonly clearCoatGlossinessScale?: number;
  readonly alphaCutoff?: number;
  readonly cutoff?: number;
  readonly useVertexColor?: number;
}

interface UnityNamedFloat {
  readonly name: string;
  readonly val: number;
}

interface UnityNamedString {
  readonly name: string;
  readonly val: string;
}

function colorMapToHex(color?: ReadonlyArray<UnityNamedFloat>): string | null {
  if (!color) return null;
  const channel = (name: string) => color.find((entry) => entry.name === name)?.val ?? 0;
  return `#${toHex(channel("r"))}${toHex(channel("g"))}${toHex(channel("b"))}`;
}

function alphaFromColorMap(color: ReadonlyArray<UnityNamedFloat> | undefined, fallback: number): number {
  return color?.find((entry) => entry.name === "a")?.val ?? fallback;
}

function stringMapFromUnityPairs(
  pairs?: ReadonlyArray<UnityNamedString>,
): Readonly<Record<string, string>> {
  if (!pairs) return {};
  const out: Record<string, string> = {};
  for (const pair of pairs) {
    if (pair.name && pair.val) out[pair.name] = pair.val;
  }
  return out;
}

function vector2FromUnityPairs(
  pairs: ReadonlyArray<UnityNamedFloat> | undefined,
  fallback: Readonly<{ x: number; y: number }>,
): Readonly<{ x: number; y: number }> {
  if (!pairs) return fallback;
  const value = (name: string, defaultValue: number) =>
    pairs.find((entry) => entry.name === name)?.val ?? defaultValue;
  return { x: value("x", fallback.x), y: value("y", fallback.y) };
}

/** Row float -> inspector percent. Unrounded — see {@link percentToNormalized}. */
function normalizedToPercent(value: number | undefined): number {
  return Number.isFinite(value) ? (value as number) * 100 : 0;
}

function vector2FromMaterialMap(
  value: unknown,
  fallback: Readonly<{ x: number; y: number }>,
): Readonly<{ x: number; y: number }> {
  const map = materialFloatMap(value);
  return {
    x: typeof map.x === "number" ? map.x : fallback.x,
    y: typeof map.y === "number" ? map.y : fallback.y,
  };
}

function colorFromString(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return hslToHex(hue, 46, 56);
}

function hslToHex(h: number, s: number, l: number): string {
  const saturation = s / 100;
  const lightness = l / 100;
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = lightness - c / 2;
  const [r, g, b] = h < 60
    ? [c, x, 0]
    : h < 120
      ? [x, c, 0]
      : h < 180
        ? [0, c, x]
        : h < 240
          ? [0, x, c]
          : h < 300
            ? [x, 0, c]
            : [c, 0, x];
  return `#${toHex(r + m)}${toHex(g + m)}${toHex(b + m)}`;
}

function toHex(value: number): string {
  return Math.round(value * 255).toString(16).padStart(2, "0");
}

/**
 * A texture id the app already ships, resolved to its `public/` URL.
 *
 * The same split `CampfireMaterialBuilder.bundledTextureUrl` makes: a
 * `MaterialData.Textures` value is either a bundled material-library FILENAME
 * (`Metal_M.png`, shipped under `public/materials/textures`) or an opaque
 * customer GUID whose bytes live in S3. Duplicated rather than shared because
 * the two layers must not import each other — `src/models/**` may never reach
 * into `src/runtime/**` — and both are three lines over one regex. If the rule
 * changes, change it in both.
 */
function bundledMaterialTextureUrl(textureId: string): string | null {
  if (/^(blob:|data:|https?:\/\/|\/)/i.test(textureId)) return textureId;
  if (!/\.(png|jpe?g|webp)$/i.test(textureId)) return null;
  return `${MATERIAL_TEXTURE_BASE_URL}/${encodeURIComponent(textureId)}`;
}
